import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

/**
 * PayPal redirects the customer back here after they approve the subscription
 * on paypal.com. We poll our sync endpoint until the webhook (or our fallback
 * fetch) marks the subscription active.
 */
export default function PaymentPayPalReturn() {
  const [params] = useSearchParams();
  // PayPal appends ?subscription_id=... automatically to the return_url.
  const subId = params.get("subscription_id") || params.get("sub_id");
  const [status, setStatus] = useState({ status: "pending" });
  const [ticks, setTicks] = useState(0);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!subId || ticks > 30) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/paypal/sync/${subId}`);
        setStatus(data);
        if (data?.status === "active") return;
      } catch (e) {
        // 404 while the tx row is still being written — keep polling briefly.
        if (ticks > 5) setErr("PayPal is taking longer than usual. Refresh in a minute.");
      }
      setTicks((n) => n + 1);
    }, 2000);
    return () => clearTimeout(t);
  }, [subId, ticks]);

  const active = status.status === "active";

  return (
    <div className="max-w-[720px] mx-auto px-6 lg:px-12 py-24" data-testid="paypal-return-page">
      <div className="surface-card p-10 text-center">
        {active ? (
          <>
            <CheckCircle2 size={72} className="mx-auto text-green-400 mb-4" />
            <h1 className="font-display text-4xl mb-3">MEMBERSHIP ACTIVE</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm mb-6">
              Thanks for supporting the show. Your perks are unlocked in your Memberships dashboard.
            </p>
            <div className="flex justify-center gap-3">
              <Link to="/memberships" className="btn-primary" data-testid="paypal-return-memberships">Go to Memberships</Link>
              <Link to="/shows" className="btn-ghost" data-testid="paypal-return-shows">Browse More Shows</Link>
            </div>
          </>
        ) : err ? (
          <>
            <AlertTriangle size={64} className="mx-auto text-yellow-400 mb-4" />
            <h1 className="font-display text-3xl mb-3">STILL PROCESSING</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm mb-4">{err}</p>
            <Link to="/memberships" className="btn-primary" data-testid="paypal-return-memberships-slow">
              Check My Memberships
            </Link>
          </>
        ) : (
          <>
            <Loader2 size={64} className="mx-auto text-[var(--ff-red)] mb-4 animate-spin" />
            <h1 className="font-display text-3xl mb-3">CONFIRMING PAYPAL…</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm">
              Waiting for PayPal to activate your subscription. This usually takes just a moment.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
